<script setup>

// Imports
import { getSwiper } from '@/utils/swiper-loader'

const props = defineProps({
  items: {
    type: Array,
    default: () => []
  }
})

const activeIndex = ref(0)
const progressPercent = ref(0)
let intervalId = null
let initPromise = null
let intersectionObserver = null

const itemsLength = computed(() => props.items.length)

const rootRef = ref(null)
const listRef = ref(null)
const itemRefs = ref([])
const mobSwiperRef = ref(null)
const swiperInstance = ref(null)
const firstMobileImageContainerRef = ref(null)
const isMobile = ref(false)
const hasEnteredViewport = ref(false)
const isFirstMobileImageReady = ref(false)
let firstMobileImageElement = null

const ROTATION_MS = 7500
const TICK_MS = 5
const TICK_PERCENT = (TICK_MS / ROTATION_MS) * 100

const startRotation = () => {
  if (!itemsLength.value) return
  if (intervalId) return

  if (isMobile.value && swiperInstance.value?.autoplay && hasEnteredViewport.value) {
    swiperInstance.value.autoplay.start()
  }

  intervalId = setInterval(() => {
    if (!itemsLength.value) return

    const nextProgress = progressPercent.value + TICK_PERCENT

    if (nextProgress >= 100) {
      if (isMobile.value) {
        // On mobile Swiper switches slide asynchronously; keep 100% until active index actually changes.
        progressPercent.value = 100
        return
      }
      progressPercent.value = 0
      activeIndex.value = (activeIndex.value + 1) % itemsLength.value
    } else {
      progressPercent.value = nextProgress
    }
  }, TICK_MS)
}

const stopRotation = () => {
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }

  if (isMobile.value && swiperInstance.value?.autoplay) {
    swiperInstance.value.autoplay.stop()
  }
}

const checkWidth = () => {
  if (typeof window === 'undefined') return
  isMobile.value = window.innerWidth < 1024
}

const markFirstMobileImageReady = async () => {
  if (isFirstMobileImageReady.value) return
  isFirstMobileImageReady.value = true

  await nextTick()
  if (isMobile.value) {
    await initSwiper()
  }
}

const detachFirstMobileImageListeners = () => {
  if (!firstMobileImageElement) return
  firstMobileImageElement.removeEventListener('load', markFirstMobileImageReady)
  firstMobileImageElement.removeEventListener('error', markFirstMobileImageReady)
  firstMobileImageElement = null
}

const watchFirstMobileImage = () => {
  if (typeof window === 'undefined') return
  if (!firstMobileImageContainerRef.value) return
  if (isFirstMobileImageReady.value) return

  detachFirstMobileImageListeners()

  const img = firstMobileImageContainerRef.value.querySelector('img')
  if (!img) return

  if (img.complete) {
    markFirstMobileImageReady()
    return
  }

  firstMobileImageElement = img
  firstMobileImageElement.addEventListener('load', markFirstMobileImageReady, { once: true })
  firstMobileImageElement.addEventListener('error', markFirstMobileImageReady, { once: true })
}

const setFirstMobileImageContainer = (el) => {
  firstMobileImageContainerRef.value = el
  watchFirstMobileImage()
}

const destroySwiper = () => {
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
    swiperInstance.value = null
  }
}

const initSwiper = async () => {
  if (!isMobile.value || !mobSwiperRef.value || swiperInstance.value) return
  if (!isFirstMobileImageReady.value) return
  if (initPromise) return initPromise

  initPromise = (async () => {
    const { Swiper, modules } = await getSwiper(['Autoplay'])
    if (!isMobile.value || !mobSwiperRef.value || swiperInstance.value) return

    swiperInstance.value = new Swiper(mobSwiperRef.value, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 350,
      autoHeight: true,
      allowTouchMove: true,
      loop: itemsLength.value > 1,
      autoplay: {
        delay: ROTATION_MS,
        // delay: 99999,
        disableOnInteraction: false
      },
      on: {
        activeIndexChange(swiper) {
          activeIndex.value = swiper.realIndex
          progressPercent.value = 0
        }
      },
      modules
    })

    if (!hasEnteredViewport.value) {
      swiperInstance.value.autoplay.stop()
    }
  })()

  await initPromise
  initPromise = null
}

const setActiveIndex = (index) => {
  if (index < 0 || index >= itemsLength.value) return
  if (index === activeIndex.value) return

  if (isMobile.value && swiperInstance.value) {
    swiperInstance.value.slideToLoop(index)
  }

  activeIndex.value = index
  progressPercent.value = 0
  stopRotation()
  startRotation()
}

const initIntersectionObserver = () => {
  if (typeof window === 'undefined') return
  if (!rootRef.value) return

  intersectionObserver = new IntersectionObserver(async ([entry]) => {
    if (!entry) return

    if (entry.isIntersecting) {
      hasEnteredViewport.value = true
      if (isMobile.value && !swiperInstance.value) {
        await initSwiper()
      }
      if (!intervalId) {
        startRotation()
      }
    }
  }, {
    threshold: 0.35
  })

  intersectionObserver.observe(rootRef.value)
}

onMounted(async () => {
  checkWidth()
  await nextTick()
  watchFirstMobileImage()
  if (isMobile.value) {
    if (isFirstMobileImageReady.value) {
      await initSwiper()
    }
  } else {
    startRotation()
  }
  initIntersectionObserver()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onBeforeUnmount(() => {
  stopRotation()
  destroySwiper()
  detachFirstMobileImageListeners()
  if (intersectionObserver) {
    intersectionObserver.disconnect()
    intersectionObserver = null
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

const scrollActiveItemIntoCenter = async () => {
  if (typeof window === 'undefined') return
  if (window.innerWidth >= 1024) return

  await nextTick()

  const container = listRef.value
  const activeEl = itemRefs.value?.[activeIndex.value]
  if (!container || !activeEl) return

  const containerWidth = container.clientWidth
  if (!containerWidth) return

  const itemCenter = activeEl.offsetLeft + activeEl.offsetWidth / 2
  const maxScrollLeft = container.scrollWidth - containerWidth
  const targetScrollLeft = Math.min(
    Math.max(itemCenter - containerWidth / 2, 0),
    Math.max(maxScrollLeft, 0)
  )

  container.scrollTo({
    left: targetScrollLeft,
    behavior: 'smooth'
  })
}

const handleResize = () => {
  if (typeof window === 'undefined') return

  checkWidth()

  if (window.innerWidth >= 1024) {
    destroySwiper()
    if (!intervalId) startRotation()
  } else {
    initSwiper()
    if (hasEnteredViewport.value && !intervalId) startRotation()
  }
}

watch(activeIndex, () => {
  scrollActiveItemIntoCenter()
})

watch(() => props.items, () => {
  if (activeIndex.value >= itemsLength.value) {
    activeIndex.value = Math.max(itemsLength.value - 1, 0)
  }
  progressPercent.value = 0
  isFirstMobileImageReady.value = false
  detachFirstMobileImageListeners()
  if (isMobile.value) {
    destroySwiper()
    nextTick(() => {
      watchFirstMobileImage()
      if (isFirstMobileImageReady.value) {
        initSwiper()
      }
    })
  }
}, { deep: true })
</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<div ref="rootRef" class="industry-showcase__list-wrapper">
  <div ref="listRef" class="industry-showcase__list">
    <button type="button"
      v-for="(item, index) in props.items"
      :key="item.id"
      :aria-label="item.title"
      :ref="(el) => { if (el) itemRefs[index] = el }"
      class="industry-showcase__list-item"
      :class="{ '--active': index === activeIndex }"
      :style="index === activeIndex ? { '--progress-width': `${progressPercent}%` } : null"
      @click="setActiveIndex(index)">

      <span v-if="item.iconField?.length" class="industry-showcase__list-item-icon">
        <img :src="item.iconField[0].url"
          alt="" width="26" height="26" loading="lazy" aria-hidden="true">
      </span>

      <span class="industry-showcase__list-item-title">
        {{ item.title }}
      </span>

      <a
        v-if="item.linkTitle?.url && item.linkTitle?.title"
        :href="item.linkTitle.url"
        :aria-label="`${item.linkTitle.title} ${item.title}`"
        class="industry-showcase__list-item-link"
        :tabindex="index !== activeIndex ? -1 : undefined"
      >
        {{ item.linkTitle.title }}
      </a>

    </button>
  </div>

  <div class="industry-showcase__active-item-panel">
    <transition v-if="!isMobile" name="industry-showcase-fade">
      <div
        v-if="props.items[activeIndex]"
        :key="props.items[activeIndex]?.id || activeIndex"
        class="industry-showcase__active-item"
      >
        <div class="industry-showcase__active-item-img">
          <GlobalsImage
            :landscape="props.items[activeIndex].imageDesktop"
            :portrait="props.items[activeIndex].imageMobile"
            :title="props.items[activeIndex].title"
            :max-width="866"
            :max-width-mobile="398"
          />
          <div
            v-if="props.items[activeIndex].iconField2 && props.items[activeIndex].textBlock"
            class="industry-showcase__active-item-info"
          >
            <div class="industry-showcase__active-item-info-img">
              <img :src="props.items[activeIndex].iconField2[0].url"
                alt="" width="62" height="62" loading="lazy" aria-hidden="true">
            </div>
            <div
              class="industry-showcase__active-item-info-description"
              v-html="props.items[activeIndex].textBlock"
            >
            </div>
          </div>
        </div>
      </div>
    </transition>

    <div v-else ref="mobSwiperRef" class="swiper industry-showcase__swiper">
      <div class="swiper-wrapper">
        <div
          v-for="(item, index) in props.items"
          :key="item.id"
          class="swiper-slide"
        >
          <div class="industry-showcase__mobile-item">
            <div
              class="industry-showcase__active-item-img"
              :ref="index === 0 ? setFirstMobileImageContainer : null"
            >
              <GlobalsImage
                :landscape="item.imageDesktop"
                :portrait="item.imageMobile"
                :title="item.title"
                :max-width="866"
                :max-width-mobile="398"
                :loading="index < 3 ? 'eager' : 'lazy'"
              />
              <div
                v-if="props.items[activeIndex].iconField2 && props.items[activeIndex].textBlock"
                class="industry-showcase__active-item-info"
              >
                <div class="industry-showcase__active-item-info-img">
                  <img :src="props.items[activeIndex].iconField2[0].url"
                    alt="" width="62" height="62" loading="lazy" aria-hidden="true">
                </div>
                <div
                  class="industry-showcase__active-item-info-description"
                  v-html="props.items[activeIndex].textBlock"
                >
                </div>
              </div>
            </div>

            <a
              v-if="item.linkTitle?.url && item.linkTitle.title"
              :href="item.linkTitle.url"
              :aria-label="item.linkTitle.title + ' ' + item.title"
              class="industry-showcase__active-item-link"
              :tabindex="index !== activeIndex ? -1 : undefined"
            >
              {{ item.linkTitle.title }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.swiper {
  overflow: hidden;
}

.industry-showcase__list-wrapper {
  display: flex;
  flex-direction: column;
  gap: 25px;

  @media (min-width: 1024px) {
    flex-direction: row;
    justify-content: space-between;
  }
}

.industry-showcase__list {
  display: flex;
  overflow: auto;
  gap: 6px;
  -ms-overflow-style: none; /* IE/old Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }

  @media (min-width: 1024px) {
    flex-direction: column;
    gap: 0;
    width: calc(650 / 1552 * 100%);
  }
}

.industry-showcase__list-item {
  display: flex;
  align-items: center;
  position: relative;
  padding: 5px 12px 6px;

  font-size: 14px;
  line-height: 20px; /* 19.6px */
  letter-spacing: -0.28px;
  color: var(--color-black);
  opacity: 0.5;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }

  &.--active {
    cursor: default;
    font-weight: 500;
    opacity: 1;
  }

  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: transparent;
    transition: background 0.3s ease;
  }

  &.--active:before {
    background: var(--color-gray-150);
  }

  &.--active:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: var(--progress-width);
    height: 2px;
    background: var(--color-black);
    transition: background 0.3s ease;
  }

  @media (min-width: 1024px) {
    padding: 20px 20px 20px 0;

    font-size: 24px;
    font-weight: 500;
    line-height: 120%; /* 28.8px */
    letter-spacing: -0.48px;

    &:hover {
      font-weight: 500;
      opacity: 0.9;
    }

    &:before,
    &:after {
      height: 3px !important;
      top: 0;
      bottom: auto;
    }

    &.--active {
      color: var(--color-gray-700);
    }

    &.--active:after {
      background: var(--color-gray-600);
    }
  }
}

@media (max-width: 1023px) {
  .industry-showcase__list-item:focus-visible {
    outline-offset: -2px;
  }
}

.industry-showcase__list-item-icon {
  display: none;

  @media (min-width: 1024px) {
    display: block;
    margin-right: 20px;
    width: 44px;
    height: 44px;
    border-radius: 9.778px;
    background: var(--color-gray-100);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
}

.industry-showcase__list-item-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.industry-showcase__list-item-link {
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    height: 42px;
    min-width: 112px;
    border: 1px solid var(--color-black);
    font-size: 16px;
    font-weight: 500;
    line-height: 140%; /* 22.4px */
    letter-spacing: -0.32px;
    color: var(--color-black);
    border-radius: 8px;
    padding-left: 16px;
    padding-right: 17px;

    opacity: 0;
    pointer-events: none;
    transition: opacity 0.5s ease, background 0.3s ease;

    & :deep(.btn-text) {
      font-size: 16px;
      line-height: 140%; /* 22.4px */
      letter-spacing: -0.32px;
    }
  }
}

.industry-showcase__list-item.--active .industry-showcase__list-item-link {
  opacity: 1;
  pointer-events: auto;

  &:hover {
    background: var(--color-gray-100);
  }
}

.industry-showcase__active-item-link {
  border: 1px solid var(--color-black);
  border-radius: 8px;
  height: 42px;
  padding: 10px 12px;
  font-size: 16px;
  font-weight: 500;
  line-height: 140%; /* 22.4px */
  letter-spacing: -0.32px;
  color: var(--color-black);
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 1024px) {
    display: none;
  }
}

.industry-showcase__active-item-link:focus-visible {
  outline-offset: -5px;
}

.industry-showcase__active-item-panel {
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-end;

  @media (min-width: 1024px) {
    width: calc(866 / 1552 * 100%);
  }
}

.industry-showcase__active-item {
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    position: relative;
    gap: 11px;
    width: 100%;
  }
}

.industry-showcase__active-item-img {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
}

.industry-showcase__active-item-info {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%,  -50%);
  padding: 30px 15px;
  max-width: 84%;
  width: 100%;
  border-radius: 12px;
  border: 0.439px solid rgba(255, 255, 255, 0.50);
  background: rgba(255, 255, 255, 0.20);
  backdrop-filter: blur(4.096357345581055px);

  @media (min-width: 1024px) {
    padding: 60px 30px;
    max-width: 67%;
    border-radius: 24px;
    border: 0.877px solid rgba(255, 255, 255, 0.50);
    backdrop-filter: blur(8.19271469116211px);
  }
}

.industry-showcase__active-item-info-img {
  width: 31px;
  height: 31px;
  margin-inline: auto;
  @media (min-width: 1024px) {
    width: 62px;
    height: 62px;
  }
}

.industry-showcase__active-item-info-description {
  margin-top: 12px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  font-family: var(--font-family-heading);
  line-height: 144%;
  color: var(--color-gray-900);
  letter-spacing: -0.32px;

  @media (min-width: 1024px) {
    margin-top: 24px;
    font-size: 24px;
    letter-spacing: -0.48px;
  }
}

.industry-showcase__mobile-item {
  display: flex !important;
  flex-direction: column;
  justify-content: flex-end;
  gap: 11px;
  width: 100%;
}

.industry-showcase__mobile-item .industry-showcase__active-item-img {
  border-radius: 16px;
  overflow: hidden;
}

.industry-showcase__swiper {
  width: 100%;
  padding-bottom: 2px;

  @media (min-width: 1024px) {
    display: none;
  }
}

.swiper-wrapper {
  display: flex;
}

.swiper-slide {
  width: 100%;
  flex-shrink: 0;
}

.industry-showcase-fade-enter-active,
.industry-showcase-fade-leave-active {
  transition: opacity 0.35s ease;
}

/* Уходящий слой вне потока — входящий сразу держит высоту панели, без «дыры» как у mode="out-in" */
.industry-showcase-fade-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.industry-showcase-fade-enter-from,
.industry-showcase-fade-leave-to {
  opacity: 0;
}

.industry-showcase-fade-enter-to,
.industry-showcase-fade-leave-from {
  opacity: 1;
}

.swiper-wrapper :deep(img) {
  width: 100% !important;
  height: auto;
}

</style>

